import { useEffect, useRef, useState } from "react";
import ExcelJS from "exceljs";
import mammoth from "mammoth/mammoth.browser";
import * as pdfjsLib from "pdfjs-dist";
import "./TrendsPage.css"; // shared chip/table/dialog primitives (tr-*)
import "./ComparePage.css";
import {
  getVerticalStacks,
  getTerritories,
  getLatestTimePeriod,
  postJobSpecComparison,
  getCompensationForBlended,
} from "./api";
import CalculationPopup from "./CalculationPopup";
import { useSearchParams } from "react-router-dom";

// Same CDN-worker approach as the old blended-roles.component.ts, pinned to
// the installed package version instead of a hardcoded one.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * ComparePage - Blended Roles (one rail item in Shell.jsx).
 *
 * React port of the old Angular blended-roles.component.ts (live code paths
 * only - the commented-out 85%-gating logic was deliberately NOT ported).
 * Flow: pick a Core Vertical Stack -> paste/drag/upload a JD -> Gemini
 * matches it against that vertical's job descriptions (POST
 * /api/job-spec-comparison) -> pick up to 3 matched roles -> blended
 * fully-loaded cost per territory with a per-cell calculation drill-down
 * modal and the shared fully-loaded-cost breakdown popup.
 *
 * Ported behaviors to be aware of:
 *  - Excel upload flattens the FIRST sheet column-wise into
 *    { camelCasedHeader: "v1 v2 v3" } and submits those fields alongside
 *    userInput (the template's columns map to the matcher's spec fields);
 *  - .docx (mammoth) and .pdf (pdfjs) extract raw text into the paste box;
 *  - blend math: normalizedPercentage = pct * (100 / totalPct); blended
 *    field = sum(roleValue * normalizedPct/100) per territory, precomputed
 *    for every territory and swapped client-side on territory change;
 *  - currency symbol is a literal £ everywhere, matching old (known
 *    limitation carried over deliberately).
 */

const COLS = [
  { field: "full_l1", header: "New to Role (NTR)" },
  { field: "full_l2", header: "Experienced (Exp)" },
  { field: "full_l3", header: "V-Experienced (Vexp)" },
];

const FIELDS_TO_BLEND = COLS.map((c) => c.field);

const fmt2 = (n) =>
  Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// convertKeysToCamelCase - verbatim rule: only -x/_x sequences are camelized.
function toCamelCase(str) {
  return str.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace("-", "").replace("_", ""));
}

export default function ComparePage() {
  const [verticalStacks, setVerticalStacks] = useState([]);
  const [verticalStackId, setVerticalStackId] = useState(null);
  const [territories, setTerritories] = useState([]);
  const [latestTimePeriodId, setLatestTimePeriodId] = useState(null);

  const [userText, setUserText] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [parsedExcelData, setParsedExcelData] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [loader, setLoader] = useState(false);

  const [jobRoles, setJobRoles] = useState([]);
  const [unmatchedRequirements, setUnmatchedRequirements] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const [roleData, setRoleData] = useState([]);
  const [normalizedRoles, setNormalizedRoles] = useState([]);
  const [blendedByTerritory, setBlendedByTerritory] = useState({});
  const [selectedTerritoryId, setSelectedTerritoryId] = useState(4); // UK default, as old

  const [modalData, setModalData] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [toasts, setToasts] = useState([]);

  const fileInputRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlStackId = searchParams.get("stack") ? Number(searchParams.get("stack")) : null;

  useEffect(() => {
    // The vertical comes from the URL (?stack=4), which the rail carries
    // over from Comp - old Minerva does the same with ?stackId=4 on its
    // blended-roles route. Survives a refresh for free.
    const sessionStackId = urlStackId;

    Promise.all([getVerticalStacks(), getTerritories(), getLatestTimePeriod()])
      .then(([stacks, terrs, latest]) => {
        // Blended Roles shows only real active stacks - NO synthetic "All"
        // (isBlendedRole=true filter in the old dropdown-section).
        const activeStacks = stacks.filter((s) => s.is_active);
        setVerticalStacks(activeStacks);
        if (sessionStackId != null && activeStacks.some((s) => s.id === sessionStackId)) {
          setVerticalStackId(sessionStackId);
        }
        setTerritories(terrs);
        setLatestTimePeriodId(latest?.id ?? null);
      })
      .catch(() => toast("error", "Failed to load reference data."));
    // Mount-only: the URL vertical is read once here; re-running on every
    // change would fight the user's own dropdown edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toast(kind, text) {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
  }

  // ---------------- file intake ----------------

  async function handleFile(file) {
    if (!file) return;
    setUploadedFileName(file.name);
    setParsedExcelData({});
    setUserText("");
    const name = file.name.toLowerCase();
    try {
      if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
        await handleExcelFile(file);
      } else if (name.endsWith(".docx")) {
        const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
        setUserText(result.value);
      } else if (name.endsWith(".pdf")) {
        const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
        let fullText = "";
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          fullText += content.items.map((it) => it.str).join(" ") + "\n";
        }
        setUserText(fullText);
      } else {
        toast("error", "Unsupported file type. Please upload Excel (.xlsx, .xls), Word (.docx), or PDF (.pdf) file.");
        setUploadedFileName("");
      }
    } catch (err) {
      console.error("File parse failed:", err);
      toast("error", "Failed to parse the uploaded file.");
      setUploadedFileName("");
    }
  }

  // Old handleExcelFile used SheetJS sheet_to_json; this reproduces the same
  // column-flatten with ExcelJS (already a dependency) - first sheet only,
  // header row keys, each column's non-empty values joined with spaces.
  async function handleExcelFile(file) {
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await file.arrayBuffer());
    const ws = wb.worksheets[0];
    if (!ws) return;
    const headers = [];
    ws.getRow(1).eachCell({ includeEmpty: false }, (cell, colNumber) => {
      headers[colNumber] = String(cell.value ?? "").trim();
    });
    const columns = {};
    ws.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
        const key = headers[colNumber];
        if (!key) return;
        const v = cell.value;
        const text =
          v && typeof v === "object"
            ? String(v.text ?? v.result ?? v.richText?.map((r) => r.text).join("") ?? "").trim()
            : String(v ?? "").trim();
        if (!text) return;
        (columns[key] = columns[key] || []).push(text);
      });
    });
    const result = {};
    Object.entries(columns).forEach(([key, values]) => {
      result[toCamelCase(key.trim())] = values.join(" ");
    });
    setParsedExcelData(result);
  }

  // ---------------- submit + response processing ----------------

  function submitInput() {
    if (!verticalStackId) {
      toast("error", "Pick a Core Vertical Stack first.");
      return;
    }
    const singleLine = userText.replace(/\s+/g, " ").trim();
    if (!singleLine && !Object.keys(parsedExcelData || {}).length) {
      toast("error", "Paste or upload a job description first.");
      return;
    }
    const payload = { ...parsedExcelData, userInput: singleLine };
    setLoader(true);
    postJobSpecComparison(payload, verticalStackId)
      .then((parsed) => {
        setLoader(false);
        setSubmitted(true);
        if (!parsed || typeof parsed !== "object") {
          toast("error", "Invalid response from server.");
          return;
        }
        if (parsed.error) {
          toast("error", parsed.error);
          return;
        }
        if (parsed.message) {
          setJobRoles([]);
          setUnmatchedRequirements([]);
          toast("info", parsed.message);
          return;
        }
        if (!parsed.primary_match && !Array.isArray(parsed.unmatched_requirements) && !Array.isArray(parsed.roles)) {
          setJobRoles([]);
          setUnmatchedRequirements([]);
          return;
        }
        processJobRoleResponse(parsed);
        setParsedExcelData({});
        setUploadedFileName("");
        setBlendedByTerritory({});
        setSelectedRoles([]);
      })
      .catch((err) => {
        console.error("Error submitting job spec:", err);
        setLoader(false);
        toast("error", "Job spec comparison failed.");
      });
  }

  // Verbatim port of processJobRoleResponse (dedupe by title, primary wins,
  // then higher pct; sort primary desc -> pct desc -> title asc).
  function processJobRoleResponse(data) {
    let rawRoles = [];
    const unmatched = [];

    if (Array.isArray(data?.roles) && data.roles.length) {
      rawRoles = data.roles.map((r) => ({
        id: r.id,
        title: r.title,
        percentage: Number(r.percentage) || 0,
        isPrimary: !!r.isPrimary,
      }));
    } else {
      if (data?.primary_match) {
        const pct = Number(String(data.primary_match.percentage).replace("%", "").trim());
        rawRoles.push({
          id: data.primary_match.id,
          title: data.primary_match.title,
          percentage: isNaN(pct) ? 0 : pct,
          isPrimary: true,
        });
      }
      (data?.unmatched_requirements || []).forEach((req) => {
        const covered = req?.covered_by;
        if (!covered) return;
        const pct = Number(String(covered.percentage).replace("%", "").trim());
        rawRoles.push({ id: covered.id, title: covered.title, percentage: isNaN(pct) ? 0 : pct, isPrimary: false });
      });
    }

    const roleMap = new Map();
    rawRoles.forEach((role) => {
      const existing = roleMap.get(role.title);
      if (!existing) roleMap.set(role.title, role);
      else if (role.isPrimary && !existing.isPrimary) roleMap.set(role.title, role);
      else if (role.isPrimary === existing.isPrimary && role.percentage > existing.percentage) roleMap.set(role.title, role);
    });

    const roles = Array.from(roleMap.values()).sort(
      (a, b) =>
        (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0) ||
        b.percentage - a.percentage ||
        a.title.localeCompare(b.title)
    );

    (data?.unmatched_requirements || []).forEach((req) => {
      if (!req?.covered_by && req?.requirement) unmatched.push(req.requirement);
    });

    setJobRoles(roles);
    setUnmatchedRequirements(unmatched);
  }

  // ---------------- role selection + blend math ----------------

  function toggleRole(role) {
    setSelectedRoles((sel) => {
      const idx = sel.findIndex((r) => r.id === role.id);
      if (idx !== -1) return sel.filter((r) => r.id !== role.id);
      if (sel.length < 3) return [...sel, role];
      toast("warn", "You can select maximum 3 roles for blending.");
      return sel;
    });
  }

  async function calculateBlendedCost() {
    const ids = selectedRoles.map((r) => r.id).join(",");
    let data;
    try {
      data = await getCompensationForBlended(ids);
    } catch {
      toast("error", "Failed to load compensation data.");
      return;
    }
    setRoleData(data);

    // Normalization: factor = 100 / sum(pct); single role = 100%.
    const totalPct = selectedRoles.reduce((s, r) => s + r.percentage, 0);
    const normalized =
      selectedRoles.length === 1
        ? [{ ...selectedRoles[0], normalizedPercentage: 100 }]
        : selectedRoles.map((r) => ({ ...r, normalizedPercentage: r.percentage * (100 / totalPct) }));
    setNormalizedRoles(normalized);

    const byTerritory = {};
    for (const t of territories) {
      const perRole = normalized.map((role) =>
        data.find((d) => d.job_title_id === role.id && d.territory_id === t.id)
      );
      if (selectedRoles.length === 1 && !perRole[0]) continue; // old single-role: skip missing territories
      const blended = {};
      for (const field of FIELDS_TO_BLEND) {
        blended[field] = perRole.reduce(
          (sum, d, i) => sum + (d?.[field] ?? 0) * (normalized[i].normalizedPercentage / 100),
          0
        );
      }
      byTerritory[t.id] = blended;
    }
    setBlendedByTerritory(byTerritory);
  }

  const blendedResult = blendedByTerritory[selectedTerritoryId] || null;
  const hasResults = Object.keys(blendedByTerritory).length > 0;

  // ---------------- calc modal + breakdown popup ----------------

  function openCalculationModal(field, header) {
    if (!selectedTerritoryId || !normalizedRoles.length || !roleData.length) return;
    const perRole = normalizedRoles.map((role) =>
      roleData.find((d) => d.job_title_id === role.id && d.territory_id === selectedTerritoryId)
    );
    const totalOriginalPercentage = selectedRoles.reduce((s, r) => s + r.percentage, 0);
    const baseField = "base_" + field.replace("full_", "");   // full_l1 -> base_l1
    const subField = field.replace("full_", "");              // full_l1 -> l1

    const calculations = normalizedRoles.map((role, i) => {
      const d = perRole[i];
      const originalValue = d?.[field] ?? 0;
      return {
        roleTitle: role.title,
        originalPercentage: role.percentage,
        normalizedPercentage: role.normalizedPercentage,
        originalValue,
        weightedContribution: originalValue * (role.normalizedPercentage / 100),
        baseValue: d?.[baseField] ?? 0,
        subTotalValue: d?.[subField] ?? 0,
      };
    });

    let weightedBase = 0;
    let weightedSub = 0;
    normalizedRoles.forEach((role, i) => {
      const w = role.normalizedPercentage / 100;
      weightedBase += Number(perRole[i]?.[baseField] ?? 0) * w;
      weightedSub += Number(perRole[i]?.[subField] ?? 0) * w;
    });

    setModalData({
      levelName: header,
      levelField: field,
      blendedValue: calculations.reduce((s, c) => s + (Number(c.weightedContribution) || 0), 0),
      calculations,
      totalOriginalPercentage,
      wasNormalized: Math.abs(totalOriginalPercentage - 100) > 0.01,
      baseAmount: weightedBase,
      subTotalAmount: weightedSub,
    });
  }

  // The shared fully-loaded-cost breakdown popup (same one the comp page
  // uses). NOTE the old comment carried over: `amount` for the breakdown is
  // the SUBTOTAL, and CalculationPopup's base_amount drives the SQL - we
  // pass the weighted BASE amount, matching how the popup's endpoints work.
  function openBreakdown(baseAmount) {
    setPopupData({
      territoryId: Number(selectedTerritoryId),
      timePeriodId: Number(latestTimePeriodId || 1),
      baseAmount: Math.round(Number(baseAmount || 0)),
      country: territories.find((t) => t.id === selectedTerritoryId)?.name || "Unknown",
    });
  }

  // ---------------- render ----------------

  return (
    <div className="cp-page">
      <div className="cp-hero">
        <div className="cp-hero-left">
          <div className="cp-eyebrow">MINERVA INTELLIGENCE &middot; COMPARE</div>
          <h1 className="cp-title">
            Blended roles, <em>one cost.</em>
          </h1>
          <p className="cp-desc">
            Paste or upload a job description; the matcher maps it to up to
            three Potentiam roles and blends their fully-loaded cost by match
            percentage, per territory.
          </p>
        </div>
      </div>

      {/* Core Vertical Stack - single-select, no "All" (must pick a real stack) */}
      <div className="cp-filterbar">
        <label className="tr-chip">
          <span className="tr-chip-label">Core Vertical Stack<span className="tr-req">*</span></span>
          <select
            className="tr-chip-select"
            value={verticalStackId ?? ""}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              setVerticalStackId(id);
              setSearchParams(!id ? {} : { stack: String(id) }, { replace: true });
            }}
          >
            <option value="">Select stack</option>
            {verticalStacks.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Drop / paste / upload area */}
      <div
        className={`cp-drop-area${isDragOver ? " drag-over" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          handleFile(e.dataTransfer?.files?.[0]);
        }}
      >
        <div className="cp-drop-head">
          <div className="cp-drop-title">Enter, drag or upload a job description</div>
          <div className="cp-drop-actions">
            <a className="cp-btn cp-btn-ghost" href="/data/BlendedRoleTemplate.xlsx" download="BlendedRoleTemplate.xlsx">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
              Download Template
            </a>
            <button type="button" className="cp-btn" onClick={() => fileInputRef.current?.click()}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>
              Upload File
            </button>
            <input ref={fileInputRef} type="file" hidden onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
        </div>
        <textarea
          className="cp-textarea"
          rows={4}
          placeholder="Paste your job description here..."
          value={userText}
          onChange={(e) => setUserText(e.target.value)}
        />
        <div className="cp-drop-foot">
          {uploadedFileName && <span className="cp-uploaded">Uploaded file: {uploadedFileName}</span>}
          <button
            type="button"
            className="cp-btn cp-btn-primary"
            onClick={submitInput}
            disabled={loader || !verticalStackId}
            title={!verticalStackId ? "Pick a Core Vertical Stack first" : undefined}
          >
            {loader ? "Matching…" : "Submit"}
          </button>
        </div>
      </div>

      {/* Role selection */}
      {jobRoles.length > 0 && (
        <div className="cp-card">
          <div className="cp-card-title">Select up to 3 job roles</div>
          {unmatchedRequirements.length > 0 && (
            <div className="cp-alert cp-alert-info">
              <strong>Unmatched requirements:</strong>
              <div className="cp-unmatched">{unmatchedRequirements.join("\n")}</div>
            </div>
          )}
          <div className="cp-role-grid">
            {jobRoles.map((role) => {
              const selected = selectedRoles.some((r) => r.id === role.id);
              return (
                <button
                  key={role.id}
                  type="button"
                  className={`cp-role-card${selected ? " selected" : ""}${role.isPrimary ? " primary" : ""}`}
                  onClick={() => toggleRole(role)}
                >
                  <div className="cp-role-title">
                    {role.title}
                    {role.isPrimary && <span className="cp-badge">Primary</span>}
                  </div>
                  <div className="cp-role-match">Match: {role.percentage}%</div>
                </button>
              );
            })}
          </div>
          <div className="cp-calc-row">
            {selectedRoles.length >= 1 ? (
              <button type="button" className="cp-btn cp-btn-primary" onClick={calculateBlendedCost}>
                {selectedRoles.length === 1
                  ? `Show Cost for ${selectedRoles[0].title}`
                  : `Calculate Blended Cost (${selectedRoles.length} roles selected)`}
              </button>
            ) : (
              <span className="cp-hint">Select at least 1 role to calculate cost</span>
            )}
          </div>
        </div>
      )}
      {submitted && jobRoles.length === 0 && unmatchedRequirements.length > 0 && (
        <div className="cp-card">
          <div className="cp-card-title">No roles available for blending</div>
          <div className="cp-alert cp-alert-info">
            <strong>Unmatched requirements:</strong>
            <div className="cp-unmatched">{unmatchedRequirements.join("\n")}</div>
          </div>
        </div>
      )}

      {/* Blended cost */}
      {hasResults && (
        <div className="cp-card">
          <div className="cp-card-title">Blended role's cost</div>
          <label className="tr-chip cp-territory">
            <span className="tr-chip-label">Territory</span>
            <select
              className="tr-chip-select"
              value={selectedTerritoryId ?? ""}
              onChange={(e) => setSelectedTerritoryId(Number(e.target.value))}
            >
              {territories.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
          <table className="tr-table cp-cost-table">
            <thead>
              <tr>
                {COLS.map((c) => (
                  <th key={c.field}>{c.header.toUpperCase()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blendedResult ? (
                <tr>
                  {COLS.map((c) => (
                    <td key={c.field}>
                      <button type="button" className="cp-cell-btn" onClick={() => openCalculationModal(c.field, c.header)}>
                        £ {fmt2(blendedResult[c.field])}
                      </button>
                    </td>
                  ))}
                </tr>
              ) : (
                <tr>
                  <td colSpan={COLS.length} className="tr-status">No data found for this territory.</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="cp-table-note">Fully-loaded annual cost · click a value for the calculation breakdown.</div>
        </div>
      )}

      {/* Calculation details modal */}
      {modalData && (
        <div className="tr-dialog-overlay" onClick={() => setModalData(null)} role="presentation">
          <div className="tr-dialog cp-modal" role="dialog" aria-label="Calculation details" onClick={(e) => e.stopPropagation()}>
            <div className="tr-dialog-head">
              <div className="tr-dialog-title">{modalData.levelName} calculation details</div>
              <button type="button" className="tr-dialog-close" aria-label="Close" onClick={() => setModalData(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>

            <div className="cp-blended-headline">
              <div className="cp-blended-label">Blended {modalData.levelName} cost</div>
              <button type="button" className="cp-blended-value" onClick={() => openBreakdown(modalData.baseAmount)}>
                £ {fmt2(modalData.blendedValue)}
              </button>
            </div>

            <table className="tr-table cp-modal-table">
              <thead>
                <tr>
                  <th>ROLE</th><th>ORIGINAL %</th><th>NORMALIZED %</th>
                  <th>{modalData.levelName.toUpperCase()} VALUE</th><th>WEIGHTED CONTRIBUTION</th>
                </tr>
              </thead>
              <tbody>
                {modalData.calculations.map((calc, i) => (
                  <tr key={i}>
                    <td>{calc.roleTitle}</td>
                    <td>{calc.originalPercentage}%</td>
                    <td>{fmt2(calc.normalizedPercentage)}%</td>
                    <td>
                      <button type="button" className="cp-cell-btn" onClick={() => openBreakdown(calc.baseValue)}>
                        £ {fmt2(calc.originalValue)}
                      </button>
                    </td>
                    <td>£ {fmt2(calc.weightedContribution)}</td>
                  </tr>
                ))}
                <tr className="cp-total-row">
                  <td>Total</td>
                  <td>{modalData.totalOriginalPercentage}%</td>
                  <td>100.00%</td>
                  <td>—</td>
                  <td>£ {fmt2(modalData.blendedValue)}</td>
                </tr>
              </tbody>
            </table>

            <div className="cp-alert cp-alert-info">
              <strong>Calculation formula:</strong> each role's contribution = (normalized % / 100) ×
              its {modalData.levelName} value; final blended cost = sum of all weighted contributions.
            </div>
            {modalData.wasNormalized && (
              <div className="cp-alert cp-alert-warn">
                Percentages were normalized to total 100%. Original total was {modalData.totalOriginalPercentage}%.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Shared fully-loaded-cost breakdown popup (same as comp page) */}
      {popupData && <CalculationPopup data={popupData} onClose={() => setPopupData(null)} />}

      {/* Toasts */}
      <div className="cp-toasts">
        {toasts.map((t) => (
          <div key={t.id} className={`cp-toast cp-toast-${t.kind}`}>{t.text}</div>
        ))}
      </div>
    </div>
  );
}
